import { useState, type ReactNode } from "react";
import { useRouter } from '#app/lib/hooks/router-compat';
import {
  Box,
  Icon,
  IconTile,
  NavPane,
  NavPaneBadge,
  NavPaneContent,
  NavPaneGroup,
  NavPaneGroupLabel,
  NavPaneHeader,
  NavPaneItem,
  NavPaneTitle,
  Text,
  type IconName,
} from "@salestrak/ui";
import { ManageDialogHeader } from "#app/components/manage-dialog-header";
import { StatusDot, statusDotToken } from "./admin-ui";
import { useAdminData } from "./use-admin-data";
import { useAdminHealth } from "./use-admin-health";
import { OverviewScreen } from "./overview-screen";
import { SuperusersScreen } from "./superusers-screen";
import { ImpersonateScreen } from "./impersonate-screen";
import { FeatureFlagsScreen } from "./feature-flags-screen";
import { IntegrationsScreen } from "./integrations-screen";
import { WorkspacesScreen } from "./workspaces-screen";
import { SystemHealthScreen } from "./system-health-screen";
import { ScheduledTasksScreen } from "./scheduled-tasks-screen";
import { useAdminTasks } from "./use-admin-tasks";
import type { Omittable } from "@salestrak/ui";

export type AdminScreen =
  | "overview"
  | "superusers"
  | "impersonate"
  | "flags"
  | "integrations"
  | "workspaces"
  | "health"
  | "tasks";

const SCREEN_TITLES: Record<AdminScreen, string> = {
  overview: "Overview",
  superusers: "Superusers",
  impersonate: "Impersonate",
  flags: "Feature flags",
  integrations: "Integrations",
  workspaces: "Workspaces",
  health: "System health",
  tasks: "Scheduled tasks",
};

function CountTag({ value }: { value: number }) {
  return (
    <Text size="xs" variant="muted" mono>
      {value}
    </Text>
  );
}

/** Nav row for this console's column — the shared dialog nav-pane row, with
 * the console's trailing count/status slot. */
function NavRow({
  icon,
  label,
  isActive,
  trailing,
  onClick,
}: Omittable<{
  icon: IconName;
  label: string;
  isActive: boolean;
  trailing?: ReactNode;
  onClick: () => void;
}>) {
  return (
    <NavPaneItem isActive={isActive} onClick={onClick}>
      <Icon name={icon} />
      {label}
      {trailing ? <NavPaneBadge>{trailing}</NavPaneBadge> : null}
    </NavPaneItem>
  );
}

export function AdminConsole({ onClose }: { onClose?: () => void }) {
  const router = useRouter();
  const [screen, setScreen] = useState<AdminScreen>("overview");
  const data = useAdminData();
  const health = useAdminHealth();
  const tasks = useAdminTasks();

  const close = onClose ?? (() => router.replace("/"));

  const healthDot = statusDotToken(
    health.total > 0,
    health.downCount > 0,
    health.degradedCount > 0,
  );
  // `tasks.mode` stays null until a response lands, so "not disabled" is not
  // the same as "running".
  const tasksDot = statusDotToken(
    tasks.mode != null,
    tasks.failedCount > 0,
    tasks.mode === "disabled" || tasks.overdueCount > 0,
  );

  return (
    <Box fill="flex" direction="row" minHeight={0}>
      {/* Sidebar — full height; it owns the console identity (tile + title)
          at the top, aligned on the content header's 48px rung. */}
      <NavPane aria-label="Admin" role="navigation">
        <NavPaneHeader>
          {/* Neutral ink on purpose — an identity mark, not a themed
              accent. */}
          <IconTile bordered name="shield" size="lg" />
          <NavPaneTitle>SalesTrak Admin</NavPaneTitle>
        </NavPaneHeader>
        <NavPaneContent>
          <NavPaneGroup>
            <NavRow
              icon="layout-dashboard"
              label="Overview"
              isActive={screen === "overview"}
              onClick={() => setScreen("overview")}
            />
          </NavPaneGroup>

          <NavPaneGroup>
            <NavPaneGroupLabel>Access</NavPaneGroupLabel>
            <NavRow
              icon="shield"
              label="Superusers"
              isActive={screen === "superusers"}
              trailing={<CountTag value={data.superusers.length} />}
              onClick={() => setScreen("superusers")}
            />
            <NavRow
              icon="user"
              label="Impersonate"
              isActive={screen === "impersonate"}
              onClick={() => setScreen("impersonate")}
            />
          </NavPaneGroup>

          <NavPaneGroup>
            <NavPaneGroupLabel>Platform</NavPaneGroupLabel>
            <NavRow
              icon="flag"
              label="Feature flags"
              isActive={screen === "flags"}
              trailing={<CountTag value={data.flags.length} />}
              onClick={() => setScreen("flags")}
            />
            <NavRow
              icon="layout-dashboard"
              label="Integrations"
              isActive={screen === "integrations"}
              trailing={<CountTag value={data.providers.length} />}
              onClick={() => setScreen("integrations")}
            />
            <NavRow
              icon="layout-grid"
              label="Workspaces"
              isActive={screen === "workspaces"}
              trailing={<CountTag value={data.workspaces.length} />}
              onClick={() => setScreen("workspaces")}
            />
          </NavPaneGroup>

          <NavPaneGroup>
            <NavPaneGroupLabel>System</NavPaneGroupLabel>
            <NavRow
              icon="activity"
              label="System health"
              isActive={screen === "health"}
              trailing={<StatusDot background={healthDot} />}
              onClick={() => setScreen("health")}
            />
            <NavRow
              icon="calendar-clock"
              label="Scheduled tasks"
              isActive={screen === "tasks"}
              trailing={<StatusDot background={tasksDot} />}
              onClick={() => setScreen("tasks")}
            />
          </NavPaneGroup>
        </NavPaneContent>
      </NavPane>

      {/* Main */}
      <Box fill="flex" minHeight={0} direction="column">
        {/* The content header names WHERE you are — the active screen; the
            console identity lives in the sidebar. */}
        <ManageDialogHeader onClose={close}>
          <Box align="center" direction="row" fill="flex" gap="sm" minWidth={0}>
            <Text size="sm" truncate={1} weight="semibold">
              {SCREEN_TITLES[screen]}
            </Text>
          </Box>
          <Text size="2xs" weight="semibold" mono color="violet">
            SUPERUSER
          </Text>
        </ManageDialogHeader>

        <Box direction="column" fill="flex" minHeight={0}>
          {screen === "overview" ? (
            <OverviewScreen
              superuserCount={data.superusers.length}
              flagCount={data.flags.length}
              workspaceCount={data.workspaces.length}
              servicesUp={health.upCount}
              servicesTotal={health.total}
              navigate={setScreen}
            />
          ) : null}
          {screen === "superusers" ? (
            <SuperusersScreen
              superusers={data.superusers}
              loading={data.superusersLoading}
              memberships={data.memberships}
              workspaces={data.workspaces}
              refetch={data.refetchSuperusers}
            />
          ) : null}
          {screen === "impersonate" ? <ImpersonateScreen /> : null}
          {screen === "flags" ? (
            <FeatureFlagsScreen
              flags={data.flags}
              loading={data.flagsLoading}
              workspaces={data.workspaces}
              refetchFlags={data.refetchFlags}
            />
          ) : null}
          {screen === "integrations" ? (
            <IntegrationsScreen
              providers={data.providers}
              loading={data.providersLoading}
              refetchProviders={data.refetchProviders}
            />
          ) : null}
          {screen === "workspaces" ? (
            <WorkspacesScreen
              workspaces={data.workspaces}
              loading={data.workspacesLoading}
            />
          ) : null}
          {screen === "tasks" ? (
            <ScheduledTasksScreen
              tasks={tasks.tasks}
              runs={tasks.runs}
              mode={tasks.mode}
              registered={tasks.registered}
              loading={tasks.loading}
              error={tasks.error}
              lastChecked={tasks.lastChecked}
              refresh={tasks.refresh}
              failedCount={tasks.failedCount}
              overdueCount={tasks.overdueCount}
            />
          ) : null}
          {screen === "health" ? (
            <SystemHealthScreen
              services={health.services}
              checking={health.checking}
              lastChecked={health.lastChecked}
              recheck={health.recheck}
              total={health.total}
              upCount={health.upCount}
              degradedCount={health.degradedCount}
              downCount={health.downCount}
              avgMs={health.avgMs}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
